import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import { rgba } from 'polished';
import breakpoint from 'styled-components-breakpoint';

import Cross from '../assets/images/cross.svg';
import Check from '../assets/images/check.svg';

import Grid from './Grid';
import Cell from './Cell';
import Block from './Block';
import Button from './Button';
import H5 from './H5';
import P from './P';
import RichTextHtml from './RichTextHtml';

const BooleanImage = styled.img`
  width: 20px;
  display: inline;
`;

const TitleColumnInner = styled(Block)`
  background-color: ${({ isSelected, theme }) =>
    isSelected ? theme.colors.white : theme.colors.grayLight};
  box-shadow: ${({ isSelected, theme }) =>
    isSelected ? `0 0 5px ${rgba(theme.colors.grayDark, 0.7)}` : 0};
  width: 100%;

  ${({ isSelectable }) => (isSelectable ? 'cursor: pointer;' : '')}

  ${breakpoint('tablet')`
    background-color: transparent;
    box-shadow: none;
  `};
`;

const Column = styled(Cell)`
  ${({ textAlign }) => (textAlign ? `text-align: ${textAlign};` : '')};
  border-right: 1px solid ${({ theme }) => theme.colors.grayDark};

  &:last-of-type {
    border-right: 0;
  }

  ${breakpoint('tablet')`
    border-right: 0;
  `};
`;

const ColumnInner = styled(Block)`
  height: 100%;
  cursor: pointer;
  opacity: ${({ isSelected }) => (isSelected ? '1' : '0.3')};

  ${breakpoint('tablet')`
    box-shadow: ${({ isSelected, theme }) =>
      isSelected ? `0 2px 5px ${rgba(theme.colors.grayDark, 0.7)}` : 'none'};
    background-color: ${({ isSelected, theme }) =>
      isSelected ? theme.colors.white : theme.colors.grayLight};
    opacity: ${({ isSelected }) => (isSelected ? '1' : '0.9')}
  `};
`;

const Row = styled(Grid)`
  position: relative;

  ${breakpoint('tablet')`
    ${Column} {
      border-bottom: 1px solid ${({ theme }) =>
        rgba(theme.colors.grayDark, 0.6)};
    }

    &:last-of-type {
      ${Column} {
        border-bottom: 0;
      }
    }
  `}
`;

const StickyWrapper = styled.div``;
const StickyGrid = styled(Grid)`
  top: 64px;
  background-color: ${({ theme }) => theme.colors.white};
  z-index: 1;

  ${breakpoint('smallTablet')`
    top: 80px;
  `}

  ${breakpoint('tablet')`
    top: 91px;
  `}
`;

const ComparisonTable = ({ content }) => {
  const [isSelectedIndex, setSelectedIndex] = useState(0);
  const numColumns = content.data.columns.length - 1;

  return (
    <>
      <StickyWrapper>
        <StickyGrid
          position="sticky"
          py={{ mobile: 'baseDown2', tablet: '0' }}
          mb="baseDown2"
        >
          {content.data.columns.map((column, i) => (
            <Cell
              key={`comparison-column-title-${i}`}
              size={{
                mobile: i === 0 ? 0 : 1 / numColumns,
                tablet: i === 0 ? 2 / 5 : 1 / (numColumns + 2),
              }}
              visible={{ mobile: i !== 0, tablet: true }}
              onClick={() => (i !== 0 ? setSelectedIndex(i - 1) : {})}
            >
              <TitleColumnInner
                p={{ mobile: 'baseUnit', tablet: 'baseDown2' }}
                ml={{ mobile: 'baseDown3', tablet: '0' }}
                mr={{ mobile: 'baseDown3', tablet: '0' }}
                isSelected={i === isSelectedIndex + 1}
                isSelectable={i !== 0}
              >
                <H5 color="black" textAlign={column.text_align}>
                  {column.column_title ? column.column_title : ' '}
                </H5>
              </TitleColumnInner>
            </Cell>
          ))}
        </StickyGrid>

        {content.data.body.map((row, rowIndex) => (
          <Fragment
            key={`comparison-table-row-${row.primary.title}-${rowIndex}`}
          >
            <P
              mb="0"
              pt="baseUnit"
              pb="baseDown2"
              textAlign="center"
              display={{ mobile: 'block', tablet: 'none' }}
            >
              {row.primary.title}
            </P>

            <Row>
              <Column
                size={{ mobile: 0, tablet: 2 / 5 }}
                p={{ mobile: 'baseUnit', tablet: 'baseDown2' }}
                visible={{ mobile: false, tablet: true }}
              >
                <P mb="0">{row.primary.title}</P>
              </Column>

              {row.items.map((item, i) => {
                const isSelected = i === isSelectedIndex;

                return (
                  <Column
                    key={`comparison-column-${i}`}
                    size={{
                      mobile: 1 / numColumns,
                      tablet: 1 / 1 / (numColumns + 2),
                    }}
                    textAlign={item.text_align || 'center'}
                    onClick={() => setSelectedIndex(i)}
                  >
                    <ColumnInner
                      isSelected={isSelected}
                      p="baseUnit"
                      ml={{ mobile: 'baseDown3', tablet: 'baseDown2' }}
                      mr={{ mobile: 'baseDown3', tablet: '0' }}
                      display="flex"
                      alignItems="center"
                      justifyContent={item.text_align || 'center'}
                    >
                      {item.text_value.html ? (
                        <RichTextHtml
                          html={item.text_value.html}
                          fontSize="baseDown1"
                        />
                      ) : null}

                      {item.boolean_value ? (
                        <BooleanImage
                          src={item.boolean_value === 'true' ? Check : Cross}
                          alt={item.boolean_value === 'true' ? 'yes' : 'no'}
                        />
                      ) : null}
                    </ColumnInner>
                  </Column>
                );
              })}
            </Row>
          </Fragment>
        ))}
      </StickyWrapper>

      <Grid>
        {content.data.columns.map((column, i) => (
          <Cell
            key={`comparison-column-title-${i}`}
            size={{
              mobile: i === 0 ? 0 : 1 / numColumns,
              tablet: i === 0 ? 2 / 5 : 1 / (numColumns + 2),
            }}
          >
            {column.cta_link && column.cta_link.url ? (
              <Block display="flex" justifyContent="center" pt="baseUnit">
                <a href={column.cta_link.url} target={column.cta_link.target}>
                  <Button
                    backgroundColor="blueDarker"
                    color="coralLight"
                    px={{ mobile: 'baseUnit', smallTablet: 'baseUp4' }}
                  >
                    {column.cta_text}
                  </Button>
                </a>
              </Block>
            ) : null}
          </Cell>
        ))}
      </Grid>
    </>
  );
};

ComparisonTable.defaultProps = {};

ComparisonTable.propTypes = {
  content: PropTypes.shape({
    data: PropTypes.shape({
      columns: PropTypes.arrayOf(
        PropTypes.shape({
          column_title: PropTypes.string,
          text_align: PropTypes.string,
          cta_text: PropTypes.string,
          cta_link: PropTypes.shape({
            url: PropTypes.string,
          }),
        }),
      ),
      body: PropTypes.arrayOf(
        PropTypes.shape({
          primary: PropTypes.shape({
            title: PropTypes.string,
            items: PropTypes.arrayOf(
              PropTypes.shape({
                boolean_value: PropTypes.string,
                text_value: PropTypes.string,
              }),
            ),
          }),
        }),
      ),
    }),
  }).isRequired,
};

export default ComparisonTable;

export const query = graphql`
  fragment ComparisonTableFragment on PrismicComparisonTable {
    id
    data {
      columns {
        column_title
        text_align
        cta_text
        cta_link {
          link_type
          url
          lang
          target
        }
      }
      body {
        ... on PrismicComparisonTableBodyRow {
          primary {
            title
          }
          items {
            boolean_value
            text_value {
              html
            }
            text_align
          }
        }
      }
    }
  }
`;
